/**
 * Fixer la date d'un sondage et l'annoncer (spec S3).
 *
 * - setFinalDate      → RPC `set_poll_final_date` (organisateur connecté ou
 *                        invité à jeton) ; clôt le sondage.
 * - subscribe         → RPC `subscribe_final_date` (votant, case non cochée
 *                        par défaut « Préviens-moi quand la date est fixée »).
 * - unsubscribe       → RPC `unsubscribe_final_date` (lien de retrait).
 * - draftAnnouncement → edge function `final-date-announcement` (IA), avec
 *                        délai court : l'appelant retombe sur le modèle de
 *                        secours si elle échoue ou tarde.
 * - notifySubscribers → edge function `send-final-date-email`.
 */
import { supabase } from "@core/lib/supabase";
import { getFingerprint, getOwnerTokenForPoll } from "@core/lib/guestIdentityService";
import { invokeSupabaseFunction } from "@core/lib/supabaseApi";
import { logError } from "@core/lib/error-handling";
import { UsageAnalyticsTracker } from "@core/services/UsageAnalyticsTracker";
import type { FinalDate } from "@core/lib/final-date";

/** Délai accordé à l'IA avant de proposer le modèle de secours. */
export const ANNOUNCEMENT_TIMEOUT_MS = 8000;

async function callerFingerprint(): Promise<string | null> {
  try {
    return await getFingerprint();
  } catch {
    return null;
  }
}

export async function setFinalDate(pollId: string, finalDate: FinalDate): Promise<FinalDate> {
  const ownerToken = getOwnerTokenForPoll(pollId);
  const fingerprint = await callerFingerprint();
  const { data, error } = await supabase.rpc("set_poll_final_date", {
    p_poll_id: pollId,
    p_final: finalDate,
    ...(ownerToken ? { p_owner_token: ownerToken } : {}),
    ...(fingerprint ? { p_fingerprint: fingerprint } : {}),
  });
  if (error || !data) {
    throw new Error(error?.message || "set_poll_final_date failed");
  }
  trackFinalDate("final_date_set", pollId, {
    all_day: !finalDate.start,
    multi_day: !!finalDate.endDate,
    has_announcement: !!finalDate.announcement,
  });
  return data as FinalDate;
}

export async function subscribeToFinalDate(
  pollId: string,
  email: string,
  language?: string,
): Promise<void> {
  const { error } = await supabase.rpc("subscribe_final_date", {
    p_poll_id: pollId,
    p_email: email,
    ...(language ? { p_language: language } : {}),
  });
  if (error) throw new Error(error.message);
  trackFinalDate("final_date_subscribed", pollId);
}

export async function unsubscribeFromFinalDate(token: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("unsubscribe_final_date", { p_token: token });
  if (error) throw new Error(error.message);
  return data === true;
}

export interface AnnouncementRequest {
  pollId: string;
  finalDate: FinalDate;
  language: string;
}

/**
 * Annonce rédigée par l'IA. Renvoie `null` (jamais d'exception) si l'IA
 * échoue, tarde ou renvoie un texte inutilisable : l'appelant affiche alors
 * le modèle de secours, que l'organisateur peut modifier de toute façon.
 */
export async function draftAnnouncement(req: AnnouncementRequest): Promise<string | null> {
  try {
    const res = await invokeSupabaseFunction<{ announcement?: string }>(
      "final-date-announcement",
      {
        pollId: req.pollId,
        language: req.language,
        finalDate: {
          date: req.finalDate.date,
          endDate: req.finalDate.endDate ?? undefined,
          start: req.finalDate.start ?? undefined,
          end: req.finalDate.end ?? undefined,
          label: req.finalDate.label ?? undefined,
          yes: req.finalDate.yes,
          maybe: req.finalDate.maybe,
          total: req.finalDate.total,
        },
        guestOwnerToken: getOwnerTokenForPoll(req.pollId) ?? undefined,
      },
      { timeout: ANNOUNCEMENT_TIMEOUT_MS, requireAuth: false },
    );
    const text = typeof res?.announcement === "string" ? res.announcement.trim() : "";
    return text.length > 0 && text.length <= 600 ? text : null;
  } catch (error) {
    logError(error, { component: "FinalDateService", operation: "draftAnnouncement" });
    return null;
  }
}

export interface NotifyResult {
  sent: number;
  skipped: number;
  failed: number;
  /**
   * Abonnés encore dus quand la boucle s'arrête (lot entièrement en échec,
   * ou plafond d'appels atteint) : ils n'ont pas reçu l'e-mail non plus.
   */
  remaining: number;
}

export async function notifySubscribers(pollId: string): Promise<NotifyResult> {
  // L'edge function envoie par lots (bornés pour tenir dans le délai d'une
  // invocation) et renvoie `remaining` : on rappelle tant qu'il en reste.
  const total: NotifyResult = { sent: 0, skipped: 0, failed: 0, remaining: 0 };
  for (let i = 0; i < 10; i++) {
    const res = await invokeSupabaseFunction<NotifyResult & { remaining?: number }>(
      "send-final-date-email",
      { pollId, guestOwnerToken: getOwnerTokenForPoll(pollId) ?? undefined },
      { timeout: 60000, requireAuth: false },
    );
    total.sent += res?.sent ?? 0;
    total.skipped += res?.skipped ?? 0;
    total.failed += res?.failed ?? 0;
    total.remaining = res?.remaining ?? 0;
    if (!res?.remaining || (res.sent ?? 0) === 0) break;
  }
  return total;
}

/**
 * Mesure (spec) : date fixée, clics « Ajouter à l'agenda », clics « Faites
 * voter votre groupe ». `feature_used` est déjà autorisé par la policy RLS
 * d'analytics_events pour anon comme pour les connectés — pas de migration.
 */
export function trackFinalDate(
  feature:
    | "final_date_set"
    | "final_date_closed_without_date"
    | "final_date_subscribed"
    | "final_date_calendar_click"
    | "final_date_share"
    | "final_date_create_cta"
    | "final_date_email_sent",
  pollId?: string | null,
  extra: Record<string, unknown> = {},
): void {
  void UsageAnalyticsTracker.track("feature_used", {
    feature,
    poll_id: pollId ?? null,
    ...extra,
  }).catch(() => {
    /* mesure non bloquante */
  });
}

// ── Disponibilité côté base ─────────────────────────────────────────────────
// Le front (Vercel) peut partir AVANT la migration 20261014120000. Tant que
// set_poll_final_date n'existe pas, « Fixer la date » échouerait : on garde
// alors l'ancien « Terminer le sondage » et on masque « Préviens-moi ».
//
// Sonde sans effet de bord : appel avec des paramètres nuls. Fonction présente
// → refus `missing_required_parameters` (22023) avant toute lecture ; absente
// → PGRST202. Résultat mémorisé pour la session.
let availability: Promise<boolean> | null = null;
let knownAvailability: boolean | null = null;

/** Valeur déjà connue (sonde terminée), sinon null. Évite un rendu de plus. */
export function knownFinalDateAvailability(): boolean | null {
  return knownAvailability;
}

export function isFinalDateAvailable(): Promise<boolean> {
  if (!availability) {
    availability = (async () => {
      try {
        const { error } = await supabase.rpc("set_poll_final_date", {
          p_poll_id: null,
          p_final: null,
        });
        const code = (error as { code?: string } | null)?.code;
        knownAvailability = !error || (code !== "PGRST202" && code !== "42883");
      } catch {
        knownAvailability = false;
      }
      return knownAvailability;
    })();
  }
  return availability;
}

/** Réservé aux tests. */
export function _resetFinalDateAvailability(): void {
  availability = null;
  knownAvailability = null;
}
